import { ApiOutlined, BellOutlined, CodeOutlined, MessageOutlined } from '@ant-design/icons-svg'
import { renderIconDefinitionToSVGElement } from '@ant-design/icons-svg/es/helpers'
import { type IconDefinition } from '@ant-design/icons-svg/es/types'
import { type IFuncUpdater } from 'ahooks-v2/lib/createUseStorageState'
import React, {
  useLayoutEffect,
  type Attributes,
  type ComponentType,
  type createElement,
  type Dispatch,
  type DOMAttributes,
  type PropsWithChildren,
  type ReactNode,
  type SetStateAction,
} from 'react'
import InquiryDropdown from '../components/Inquiry/InquiryDropdown'
import MessageCenter from '../components/MessageCenter'

// 主系统提供的工具
type Intrinsic = Record<any, (...args: any[]) => any> & { svgBaseProps: Record<string, string> }

interface SlotDefinition<P> extends Attributes {
  render: (intrinsic: Intrinsic & Pick<this, 'key'>, props?: P) => ReactNode
}

type ExtractProps<T> = T extends SlotDefinition<infer P> ? P : never

// 快速创建来自 ant-design 的图标
function createReactIcon(
  createReactElement: typeof createElement,
  IconComponent: ComponentType<DOMAttributes<HTMLElement>>,
  icon: IconDefinition,
  extraSVGAttrs: Record<string, string>,
) {
  return createReactElement(IconComponent, {
    dangerouslySetInnerHTML: {
      __html: renderIconDefinitionToSVGElement(icon, {
        extraSVGAttrs,
      }),
    },
  })
}

export function SlotProvider(props: PropsWithChildren<unknown>) {
  const [inquirySlot] = useInquirySlot()
  const [messageCenterSlot] = useMessageCenterSlot()
  useLayoutEffect(() => {
    if (window.__POWERED_BY_QIANKUN__) {
      window.qiankunProps?.updateConfig({
        slots: [inquirySlot, messageCenterSlot],
      })
    }
  }, [inquirySlot, messageCenterSlot])
  return (
    <>
      {props.children}
      <div hidden>
        <InquiryDropdown />
        <MessageCenter />
      </div>
    </>
  )
}

export function useInquirySlot() {
  const slot: SlotDefinition<{
    setInquiryOnlineStatus: (value?: 'ONLINE' | 'OFFLINE' | IFuncUpdater<'ONLINE' | 'OFFLINE'>) => void
    inquiryOnlineStatus: 'ONLINE' | 'OFFLINE'
    setConsoleVisible: Dispatch<SetStateAction<boolean>>
    unread: number
  }> = {
    key: 'INQUIRY',
    render: ({ key, createReactElement, Dropdown, Badge, Button, Icon, svgBaseProps }, props) =>
      createReactElement(
        Badge,
        {
          key,
          count: props?.inquiryOnlineStatus === 'ONLINE' ? (props?.unread ?? 0) : 0,
        },
        createReactElement(
          Dropdown,
          {
            menu: {
              items: [
                {
                  key: 'online',
                  label: `设为${props?.inquiryOnlineStatus === 'ONLINE' ? '离线' : '在线'}`,
                  icon: createReactIcon(createReactElement, Icon, ApiOutlined, svgBaseProps),
                },
                {
                  key: 'console',
                  label: '咨询服务台',
                  icon: createReactIcon(createReactElement, Icon, CodeOutlined, svgBaseProps),
                },
              ],
              onClick({ key }: Attributes) {
                if (key === 'online') {
                  props?.setInquiryOnlineStatus(props?.inquiryOnlineStatus === 'ONLINE' ? 'OFFLINE' : 'ONLINE')
                }
                if (key === 'console') {
                  props?.setConsoleVisible(true)
                }
              },
            },
          },
          createReactElement(Button, {
            icon: createReactIcon(createReactElement, Icon, MessageOutlined, svgBaseProps),
          }),
        ),
      ),
  }
  const update = ({ setInquiryOnlineStatus, inquiryOnlineStatus, setConsoleVisible, unread }: ExtractProps<typeof slot>) => {
    if (window.__POWERED_BY_QIANKUN__) {
      window.qiankunProps?.updateConfig({
        slots: window.qiankunProps?.getConfig()?.slots?.map((slot: Record<string, any>) => {
          if (slot.key === 'INQUIRY') {
            let originRender: (...args: any[]) => any
            if (slot.originRender) {
              originRender = slot.originRender
            } else {
              originRender = slot.render
            }
            return {
              ...slot,
              originRender,
              render: (intrinsic: any) =>
                originRender(intrinsic, {
                  setInquiryOnlineStatus,
                  inquiryOnlineStatus,
                  setConsoleVisible,
                  unread,
                }),
            }
          }
          return slot
        }),
      })
    }
  }
  return [slot, update] as const
}

export function useMessageCenterSlot() {
  const slot: SlotDefinition<{
    count: number
    setVisible: Dispatch<SetStateAction<boolean>>
  }> = {
    key: 'MESSAGE_CENTER',
    render: ({ key, createReactElement, Tooltip, Badge, Button, Icon, svgBaseProps }, props) =>
      createReactElement(
        Tooltip,
        {
          key,
          title: '消息中心',
        },
        createReactElement(
          Badge,
          {
            count: props?.count ?? 0,
          },
          createReactElement(Button, {
            icon: createReactIcon(createReactElement, Icon, BellOutlined, svgBaseProps),
            onClick() {
              props?.setVisible(true)
            },
          }),
        ),
      ),
  }
  const update = ({ count, setVisible }: ExtractProps<typeof slot>) => {
    if (window.__POWERED_BY_QIANKUN__) {
      window.qiankunProps?.updateConfig({
        slots: window.qiankunProps?.getConfig()?.slots?.map((slot: Record<string, any>) => {
          if (slot.key === 'MESSAGE_CENTER') {
            let originRender: (...args: any[]) => any
            if (slot.originRender) {
              originRender = slot.originRender
            } else {
              originRender = slot.render
            }
            return {
              ...slot,
              originRender,
              render: (intrinsic: any) =>
                originRender(intrinsic, {
                  count,
                  setVisible,
                }),
            }
          }
          return slot
        }),
      })
    }
  }
  return [slot, update] as const
}
